import React, { useEffect, useRef, useState } from 'react';
import Lenis from 'lenis';
import { watchDoc } from '../../firestoreLive';
import { PERSONAL_INFO, SOCIAL_LINKS } from '../../constants';
import { SocialLink } from '../../types';

import Preloader from './Preloader';
import NavV2 from './NavV2';
import CursorV2 from './CursorV2';
import { playClick, playHover, primeAudio } from './sfx';
import HeroSection from './HeroSection';
import ReelPlayer, { MORPH_DISTANCE } from './ReelPlayer';
import WorkGrid from './WorkGrid';
import AboutSection from './AboutSection';
import ExperienceSection from './ExperienceSection';
import VibeCodingSection from './VibeCodingSection';
import ContactSection from './ContactSection';
import { startAnalytics } from '../../analytics';

// No hard-coded playback id. A fallback here would mean that clearing or
// mistyping the reel in the admin panel silently played a build-time asset
// instead, which looks like the panel is broken rather than empty.

/** The rebuilt portfolio — Lusion-school architecture. */
const SiteV2: React.FC = () => {
  const lenisRef = useRef<Lenis | null>(null);
  const [sceneReady, setSceneReady] = useState(false);
  const [started, setStarted] = useState(false);
  // The reel's rewind rides the curtain, not the handover — see ReelPlayer.
  const [rewindCue, setRewindCue] = useState(false);
  const [theater, setTheater] = useState(false);
  const theaterRef = useRef(false);
  theaterRef.current = theater;
  const [muxId, setMuxId] = useState('');
  const [email, setEmail] = useState(PERSONAL_INFO.email);
  const [phone, setPhone] = useState(PERSONAL_INFO.phone);
  const [socials, setSocials] = useState<SocialLink[]>(SOCIAL_LINKS);

  // One soft synthesized click, and a lighter tick on hover, for every
  // interactive element — delegated, so components need no wiring of their own.
  // First-party visitor analytics. Deferred internally so it never competes
  // with the first paint; failures are swallowed so it can't break the site.
  useEffect(() => {
    startAnalytics();
  }, []);

  useEffect(() => {
    const INTERACTIVE = 'a, button, [role="button"], [data-cursor]';

    // audio cannot start before a gesture; wake it on the very first one so
    // hover ticks are audible from then on
    const prime = () => primeAudio();
    window.addEventListener('pointerdown', prime, { once: true, passive: true });
    window.addEventListener('keydown', prime, { once: true, passive: true });

    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest(INTERACTIVE)) playClick();
    };

    // fine pointers only: a touch "hover" would fire on every tap
    const fine = window.matchMedia('(pointer: fine)').matches;
    let lastHit: Element | null = null;
    let lastAt = 0;
    const onOver = (e: PointerEvent) => {
      const hit = (e.target as HTMLElement | null)?.closest(INTERACTIVE) ?? null;
      // only when the target actually changes, so moving within a button is silent
      if (!hit || hit === lastHit) {
        if (!hit) lastHit = null;
        return;
      }
      lastHit = hit;
      const now = performance.now();
      if (now - lastAt < 70) return;
      lastAt = now;
      playHover();
    };

    window.addEventListener('pointerdown', onDown, { passive: true });
    if (fine) window.addEventListener('pointerover', onOver, { passive: true });
    return () => {
      window.removeEventListener('pointerdown', prime);
      window.removeEventListener('keydown', prime);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerover', onOver);
    };
  }, []);


  // Always open at the top. The browser restoring a mid-page scroll would also
  // desync the hero measurement the reel morph depends on.
  //
  // scrollRestoration does that on its own. There was a `beforeunload` handler
  // scrolling to 0 here as well, which read as harmless belt-and-braces and was
  // not: a mailto: or tel: link fires beforeunload as the browser hands off to
  // the OS, but the page is never unloaded, so it just stayed there — scrolled
  // back to the top under the visitor, with the reel flying out of its dock and
  // home to the hero slot. Tapping the contact card's email or phone threw you
  // to the top of the site and looked like the reel maximising itself.
  useEffect(() => {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
  }, []);

  // Never rest half-morphed. The reference does exactly this: if scrolling
  // stops while the reel is between hero and dock, the page finishes the
  // journey on its own in whichever direction you were last heading.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastY = window.scrollY;
    let dir = 1;
    const onScroll = () => {
      const yNow = window.scrollY;
      if (yNow !== lastY) dir = yNow > lastY ? 1 : -1;
      lastY = yNow;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (theaterRef.current) return;
        const vh = window.innerHeight;
        const p = window.scrollY / (vh * MORPH_DISTANCE);
        if (p > 0.02 && p < 0.98) {
          lenisRef.current?.scrollTo(dir > 0 ? vh * MORPH_DISTANCE : 0, {
            duration: 0.85,
            easing: (t: number) => 1 - Math.pow(1 - t, 4),
          });
        }
      }, 140);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Smooth scroll
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;
    lenis.scrollTo(0, { immediate: true });
    let rafId = 0;
    const raf = (time: number) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  // Lock scroll under the preloader
  useEffect(() => {
    if (started) {
      lenisRef.current?.start();
      document.body.style.overflow = '';
    } else {
      lenisRef.current?.stop();
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [started]);

  // A link to a section (motion.hamlt.com/#vibecoding) lands on it. The page
  // still opens at the top — the reel morph is measured from there — and only
  // travels down once the preloader has lifted, so the reel docks on the way
  // like any scroll. Editing the hash, or back and forward, does the same.
  //
  // The target is a number taken from the window, not the element: on a hash
  // change the browser has already jumped there itself, and Lenis measuring
  // the element against its own, not yet updated, position sent the page back
  // where it was. Images loading on the way can move the section, so the
  // landing is checked once more.
  useEffect(() => {
    if (!started) return;
    const elFor = () => {
      const id = decodeURIComponent(location.hash.slice(1));
      return id && id !== 'top' ? document.getElementById(id) : null;
    };
    // once only: a section near the foot of the page never reaches the top
    const land = (el: HTMLElement, duration?: number, retry = true) =>
      lenisRef.current?.scrollTo(el.getBoundingClientRect().top + window.scrollY, {
        ...(duration ? { duration } : { immediate: true }),
        force: true,
        onComplete: () => {
          if (retry && Math.abs(el.getBoundingClientRect().top) > 2) land(el, undefined, false);
        },
      });
    const onLoad = () => {
      const el = elFor();
      if (el) land(el, 1.4);
    };
    const onHash = () => {
      const el = elFor();
      if (el) land(el);
    };
    // two frames, so the sections below the fold have laid out first
    const raf = requestAnimationFrame(() => requestAnimationFrame(onLoad));
    window.addEventListener('hashchange', onHash);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('hashchange', onHash);
    };
  }, [started]);

  // Config from Firestore (admin panel can override the Mux playback ID).
  // Deferred: the reel's poster is preloaded and its <video> mounts without a
  // playback id, so the hero does not wait on this. See firestoreLive.ts.
  useEffect(() => {
    const unsubConfig = watchDoc<{ muxHeroPlaybackId?: string }>(
      'content',
      'config',
      (c) => setMuxId(c.muxHeroPlaybackId ?? '')
    );
    const unsubProfile = watchDoc<{ email?: string; phone?: string; socials?: SocialLink[] }>(
      'content',
      'profile',
      (p) => {
        if (p.email) setEmail(p.email);
        if (p.phone) setPhone(p.phone);
        if (Array.isArray(p.socials)) setSocials(p.socials);
      }
    );
    return () => {
      unsubConfig();
      unsubProfile();
    };
  }, []);

  const scrollTo = (selector: string) => {
    const el = selector === '#top' ? 0 : (document.querySelector(selector) as HTMLElement | null);
    if (el === null) return;
    lenisRef.current?.scrollTo(el as any, { offset: 0, duration: 1.4 });
  };

  return (
    <div className="bg-paper text-ink font-grotesk min-h-screen w-full relative">
      <CursorV2 />
      <Preloader
        ready={sceneReady}
        onBeforeLift={() => setRewindCue(true)}
        onDone={() => setStarted(true)}
        chromeHidden={theater}
        onLogoClick={() => scrollTo('#top')}
      />
      <NavV2
        email={email}
        phone={phone}
        socials={socials}
        scrollTo={scrollTo}
        started={started}
        hidden={theater}
      />
      {/* rendered outside <main> so no transformed ancestor can trap the fixed player */}
      <ReelPlayer playbackId={muxId} onReady={() => setSceneReady(true)} onTheater={setTheater} rewindCue={rewindCue} />

      <main>
        <HeroSection started={started} title="Motion Graphics Designer" />
        <WorkGrid />
        <VibeCodingSection scrollTo={scrollTo} theater={theater} />
        <AboutSection />
        <ExperienceSection />
        <ContactSection />
      </main>
    </div>
  );
};

export default SiteV2;
